import React from 'react';
import { FiBarChart } from 'react-icons/fi';
import LessonTemplate from './components/LessonTemplate';
import QuantLessonPracticeSlot from './components/QuantLessonPracticeSlot';

const ProbabilitiesLesson = () => {
  const sections = [
    {
      id: 'basic-probability',
      title: 'Basic Probability',
      content: {
        introduction:
          'Probability measures the likelihood of an event occurring. It ranges from 0 (impossible) to 1 (certain), and is often expressed as a fraction, decimal, or percentage.',

        concepts: [
          {
            title: 'Fundamental Probability Formula',
            explanation:
              '§P(\\text{event}) = \\frac{\\text{Number of favorable outcomes}}{\\text{Total number of possible outcomes}}§. The probability must be between 0 and 1.',
            example: {
              problem:
                'A fair die is rolled. What is the probability of rolling a 4?',
              solution: [
                'Favorable outcomes: rolling a 4 = 1 way',
                'Total possible outcomes: 6 faces on die',
                '§P(\\text{rolling } 4) = \\frac{1}{6}§',
                'As a decimal: §\\approx 0.167§ or about 16.7%',
              ],
            },
          },
          {
            title: 'Complementary Probability',
            explanation:
              '§P(\\text{not } A) = 1 - P(A)§. The probability of an event not happening is 1 minus the probability of it happening.',
            example: {
              problem:
                'If the probability of rain tomorrow is 0.3, what is the probability it will NOT rain?',
              solution: [
                '§P(\\text{rain}) = 0.3§',
                '§P(\\text{no rain}) = 1 - P(\\text{rain})§',
                '§P(\\text{no rain}) = 1 - 0.3 = 0.7§',
                'There is a 70% chance it will not rain',
              ],
            },
          },
          {
            title: 'Multiple Events',
            explanation:
              'When counting favorable outcomes, be systematic. Use organized lists, tree diagrams, or counting principles.',
            example: {
              problem:
                'Two coins are flipped. What is the probability of getting exactly one head?',
              solution: [
                'List all outcomes: HH, HT, TH, TT',
                'Total outcomes: 4',
                'Favorable outcomes (exactly 1 head): HT, TH = 2',
                '§P(\\text{exactly 1 head}) = \\frac{2}{4} = \\frac{1}{2} = 0.5§',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'compound-probability',
      title: 'Compound Probability',
      content: {
        introduction:
          'Compound probability deals with multiple events. We use "AND" (multiplication) and "OR" (addition) rules to calculate probabilities of combined events.',

        concepts: [
          {
            title: 'AND Rule (Independent Events)',
            prerequisiteNote:
              'Use this only when one event does not change the probability of the other.',
            vocabulary: ['independent', 'AND', 'multiply', 'replacement'],
            explanation:
              'For independent events A and B: §P(A \\text{ AND } B) = P(A) \\times P(B)§. Events are independent if one does not affect the other.',
            microExample: {
              problem: 'Flip a coin and roll a die. What is P(heads and 6)?',
              solution: ['§P(H)=1/2§', '§P(6)=1/6§', 'Multiply: §1/12§'],
            },
            greTrap:
              'If the problem says “without replacement,” the events are usually dependent, not independent.',
            checkQuestion:
              'Does drawing two cards without replacement keep the probabilities the same?',
            example: {
              problem:
                'A coin is flipped and a die is rolled. What is the probability of getting heads AND rolling a 5?',
              solution: [
                '§P(\\text{heads}) = \\frac{1}{2}§',
                '§P(\\text{rolling } 5) = \\frac{1}{6}§',
                'These events are independent',
                '§P(\\text{heads AND } 5) = \\frac{1}{2} \\times \\frac{1}{6} = \\frac{1}{12}§',
              ],
            },
          },
          {
            title: 'OR Rule (Mutually Exclusive)',
            explanation:
              'For mutually exclusive events A and B: §P(A \\text{ OR } B) = P(A) + P(B)§. Events are mutually exclusive if they cannot both occur.',
            example: {
              problem:
                'A card is drawn from a deck. What is the probability of getting a King OR a Queen?',
              solution: [
                '§P(\\text{King}) = \\frac{4}{52} = \\frac{1}{13}§',
                '§P(\\text{Queen}) = \\frac{4}{52} = \\frac{1}{13}§',
                'These are mutually exclusive (card cannot be both)',
                '§P(\\text{King OR Queen}) = \\frac{1}{13} + \\frac{1}{13} = \\frac{2}{13}§',
              ],
            },
          },
          {
            title: 'OR Rule (Not Mutually Exclusive)',
            explanation:
              'For non-mutually exclusive events: §P(A \\text{ OR } B) = P(A) + P(B) - P(A \\text{ AND } B)§. Subtract the overlap to avoid double counting.',
            example: {
              problem:
                'What is the probability of drawing a heart OR a face card from a standard deck?',
              solution: [
                '§P(\\text{heart}) = \\frac{13}{52} = \\frac{1}{4}§',
                '§P(\\text{face card}) = \\frac{12}{52} = \\frac{3}{13}§',
                '§P(\\text{heart AND face card}) = \\frac{3}{52}§ (J, Q, K of hearts)',
                '§P(\\text{heart OR face card}) = \\frac{13}{52} + \\frac{12}{52} - \\frac{3}{52} = \\frac{22}{52} = \\frac{11}{26}§',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'conditional-probability',
      title: 'Conditional Probability',
      content: {
        introduction:
          'Conditional probability is the probability of an event occurring given that another event has already occurred. This is written as P(A|B), read as "probability of A given B."',

        concepts: [
          {
            title: 'Conditional Probability Formula',
            explanation:
              '§P(A|B) = \\frac{P(A \\text{ AND } B)}{P(B)}§. This gives the probability of A occurring when we know B has occurred.',
            example: {
              problem:
                'In a class, 60% of students passed Math, 50% passed English, and 30% passed both. If a student passed Math, what is the probability they also passed English?',
              solution: [
                '§P(\\text{Math}) = 0.6§',
                '§P(\\text{English}) = 0.5§',
                '§P(\\text{both}) = 0.3§',
                '§P(\\text{English}|\\text{Math}) = \\frac{P(\\text{both})}{P(\\text{Math})}§',
                '§P(\\text{English}|\\text{Math}) = \\frac{0.3}{0.6} = 0.5§ or 50%',
              ],
            },
          },
          {
            title: 'Dependent Events',
            prerequisiteNote:
              'Use this when the first event changes the total or favorable outcomes for the second event.',
            vocabulary: ['dependent', 'without replacement', 'conditional probability'],
            explanation:
              'When events are dependent (one affects the other), probabilities change. Common in "without replacement" scenarios.',
            microExample: {
              problem:
                'A bag has 2 red and 3 blue balls. One red is removed. How many balls remain?',
              solution: [
                'Total decreases from 5 to 4.',
                'Red decreases from 2 to 1.',
                'The second probability must use the new counts.',
              ],
            },
            greTrap:
              'Do not reuse the original denominator after an item is removed.',
            checkQuestion:
              'After drawing one card without replacement, how many cards remain in the deck?',
            example: {
              problem:
                'A bag contains 3 red balls and 2 blue balls. Two balls are drawn without replacement. What is the probability both are red?',
              solution: [
                '§P(\\text{first ball red}) = \\frac{3}{5}§',
                'After removing one red: 2 red and 2 blue remain',
                '§P(\\text{second ball red|first was red}) = \\frac{2}{4} = \\frac{1}{2}§',
                '§P(\\text{both red}) = \\frac{3}{5} \\times \\frac{1}{2} = \\frac{3}{10}§',
              ],
            },
          },
          {
            title: 'Tree Diagrams',
            explanation:
              'Tree diagrams help visualize conditional probabilities by showing all possible paths and their probabilities.',
            example: {
              problem:
                'A box has 2 red and 3 green marbles. Draw one, replace it, then draw again. What is P(one of each color)?',
              solution: [
                'Two ways to get one of each: RG or GR',
                '§P(\\text{R then G}) = (\\frac{2}{5}) \\times (\\frac{3}{5}) = \\frac{6}{25}§',
                '§P(\\text{G then R}) = (\\frac{3}{5}) \\times (\\frac{2}{5}) = \\frac{6}{25}§',
                '§P(\\text{one of each}) = \\frac{6}{25} + \\frac{6}{25} = \\frac{12}{25}§',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'combinations-permutations',
      title: 'Combinations & Permutations',
      content: {
        introduction:
          'Counting techniques are essential for calculating probabilities. Permutations count ordered arrangements, while combinations count unordered selections.',

        concepts: [
          {
            title: 'Fundamental Counting Principle',
            explanation:
              'If event A can occur in m ways and event B in n ways, then both can occur in §m \\times n§ ways.',
            example: {
              problem:
                'How many different 2-letter codes can be formed using letters A, B, C for the first letter and digits 1, 2, 3, 4 for the second character?',
              solution: [
                'First position: 3 choices (A, B, or C)',
                'Second position: 4 choices (1, 2, 3, or 4)',
                'Total codes = §3 \\times 4 = 12§',
                'Examples: A1, A2, A3, A4, B1, B2, ...',
              ],
            },
          },
          {
            title: 'Permutations',
            prerequisiteNote:
              'Use permutations when order matters: ABC and BAC count as different.',
            vocabulary: ['permutation', 'order matters', 'restriction', 'slot'],
            explanation:
              '§P(n, r) = \\frac{n!}{(n-r)!}§ counts the number of ways to arrange r items from n items where order matters.',
            microExample: {
              problem:
                'How many 2-letter codes can be made from A, B, C without repetition?',
              solution: ['First slot: 3 choices', 'Second slot: 2 choices', 'Total: §3×2=6§'],
            },
            greTrap:
              'For restrictions, place the restricted items first, then fill the remaining slots.',
            checkQuestion:
              'If the first slot cannot be zero, should you fill that slot early or late?',
            example: {
              problem:
                'How many different 3-digit numbers can be formed using digits 1, 2, 3, 4, 5 without repetition?',
              solution: [
                'First digit: 5 choices',
                'Second digit: 4 choices (one used)',
                'Third digit: 3 choices (two used)',
                'Total = §5 \\times 4 \\times 3 = 60§',
                'Or using formula: §P(5,3) = \\frac{5!}{(5-3)!} = \\frac{120}{2} = 60§',
              ],
            },
          },
          {
            title: 'Combinations',
            explanation:
              '§C(n, r) = \\frac{n!}{r!(n-r)!}§ counts the number of ways to choose r items from n items where order does not matter.',
            example: {
              problem:
                'How many different 3-person committees can be formed from 5 people?',
              solution: [
                'Order does not matter (ABC = BAC = CAB)',
                'Use combinations: §C(5,3)§',
                '§C(5,3) = \\frac{5!}{3! \\times 2!}§',
                '§= \\frac{5 \\times 4 \\times 3 \\times 2 \\times 1}{(3 \\times 2 \\times 1) \\times (2 \\times 1)}§',
                '§= \\frac{120}{6 \\times 2} = \\frac{120}{12} = 10§',
              ],
            },
          },
        ],
      },
    },
  ];

  const practiceQuestions = [
    {
      type: 'timed',
      timeLimit: 90,
      question:
        'A fair coin is flipped three times. What is the probability of getting exactly two heads?',
      options: ['1/8', '3/8', '1/2', '5/8', '3/4'],
      correct: 1,
      explanation:
        'Total outcomes: §2^3 = 8§ (HHH, HHT, HTH, HTT, THH, THT, TTH, TTT). Exactly 2 heads: HHT, HTH, THH = 3 outcomes. §P(\\text{exactly 2 heads}) = \\frac{3}{8}§.',
    },
    {
      type: 'timed',
      timeLimit: 100,
      question:
        'In a bag of 12 marbles (5 red, 4 blue, 3 green), what is the probability of randomly selecting a red or green marble?',
      options: ['5/12', '1/3', '2/3', '1/2', '8/12'],
      correct: 2,
      explanation:
        'Red or green: §5 + 3 = 8§ marbles. Total: 12 marbles. §P(\\text{red or green}) = \\frac{8}{12} = \\frac{2}{3}§.',
    },
    {
      type: 'timed',
      timeLimit: 120,
      question:
        'Two dice are rolled. What is the probability that the sum is 7?',
      options: ['1/12', '1/9', '1/6', '1/3', '5/36'],
      correct: 2,
      explanation:
        'Ways to get sum of 7: (1,6), (2,5), (3,4), (4,3), (5,2), (6,1) = 6 ways. Total outcomes: §6 \\times 6 = 36§. §P(\\text{sum} = 7) = \\frac{6}{36} = \\frac{1}{6}§.',
    },
    {
      type: 'timed',
      timeLimit: 110,
      question:
        'A card is drawn from a standard 52-card deck. What is the probability of NOT drawing a spade?',
      options: ['1/4', '1/3', '1/2', '3/4', '2/3'],
      correct: 3,
      explanation:
        '§P(\\text{spade}) = \\frac{13}{52} = \\frac{1}{4}§. §P(\\text{not spade}) = 1 - \\frac{1}{4} = \\frac{3}{4}§.',
    },
    {
      type: 'timed',
      timeLimit: 130,
      question:
        'Box A has 3 red and 2 blue balls. Box B has 4 red and 1 blue ball. One ball is randomly selected from each box. What is the probability both are red?',
      options: ['7/25', '12/25', '3/5', '7/10', '4/5'],
      correct: 1,
      explanation:
        '§P(\\text{red from A}) = \\frac{3}{5}§. §P(\\text{red from B}) = \\frac{4}{5}§. Events are independent. §P(\\text{both red}) = \\frac{3}{5} \\times \\frac{4}{5} = \\frac{12}{25}§.',
    },
    {
      type: 'timed',
      timeLimit: 115,
      question:
        'A fair die is rolled twice. What is the probability that the sum is 7?',
      options: ['1/6', '1/4', '1/8', '1/12', '1/3'],
      correct: 0,
      explanation:
        'Favorable outcomes: (1,6), (2,5), (3,4), (4,3), (5,2), (6,1) = 6 outcomes. Total = 36. §P = \\frac{6}{36} = \\frac{1}{6}§.',
    },
    {
      type: 'timed',
      timeLimit: 120,
      question:
        'In a deck of 52 cards, what is the probability of drawing an Ace or a King?',
      options: ['2/13', '1/13', '4/13', '1/26', '8/52'],
      correct: 0,
      explanation:
        'Aces = 4, Kings = 4. Total favorable = 8. §P = \\frac{8}{52} = \\frac{2}{13}§.',
    },
    {
      type: 'timed',
      timeLimit: 110,
      question:
        'A bag contains 5 red, 3 blue, and 2 yellow marbles. If two marbles are drawn without replacement, what is the probability both are red?',
      options: ['1/4', '2/9', '1/5', '1/3', '4/15'],
      correct: 1,
      explanation:
        'Total: 10 marbles. §P(\\text{first red}) = \\frac{5}{10} = \\frac{1}{2}§. After removing one red: §P(\\text{second red|first red}) = \\frac{4}{9}§. §P(\\text{both red}) = \\frac{1}{2} \\times \\frac{4}{9} = \\frac{4}{18} = \\frac{2}{9}§.',
    },
    {
      type: 'timed',
      timeLimit: 120,
      question:
        'In a class of 30 students, 18 play soccer and 12 play basketball. If 8 students play both sports, what is the probability a randomly selected student plays at least one sport?',
      options: ['11/15', '3/5', '4/5', '7/10', '2/3'],
      correct: 0,
      explanation:
        'Play at least one = soccer OR basketball. §P(S \\text{ or } B) = P(S) + P(B) - P(S \\text{ and } B) = \\frac{18}{30} + \\frac{12}{30} - \\frac{8}{30} = \\frac{22}{30} = \\frac{11}{15}§.',
    },
    {
      type: 'timed',
      timeLimit: 110,
      question:
        'From a group of 6 people, how many different 2-person teams can be formed?',
      options: ['12', '15', '18', '21', '30'],
      correct: 1,
      explanation:
        'Order does not matter, use combinations: §C(6,2) = \\frac{6!}{2! \\times 4!} = \\frac{6 \\times 5}{2 \\times 1} = \\frac{30}{2} = 15§.',
    },
    {
      type: 'timed',
      timeLimit: 130,
      question:
        'A password must be 4 digits long using digits 0-9 without repetition. How many passwords start with an odd digit?',
      options: ['2520', '3024', '4536', '5040', '6048'],
      correct: 0,
      explanation:
        'First digit: 5 odd digits (1,3,5,7,9). Second digit: 9 remaining. Third: 8 remaining. Fourth: 7 remaining. Total = §5 \\times 9 \\times 8 \\times 7 = 2520§.',
    },
    {
      type: 'timed',
      timeLimit: 130,
      question:
        'A jar contains 10 balls numbered 1-10. If 3 balls are drawn without replacement, what is the probability all three numbers are even?',
      options: ['1/12', '1/10', '1/8', '1/6', '1/5'],
      correct: 0,
      explanation:
        'Even numbers: 2,4,6,8,10 = 5 balls. §P(\\text{all even}) = \\frac{C(5,3)}{C(10,3)} = \\frac{10}{120} = \\frac{1}{12}§. Or: §\\frac{5}{10} \\times \\frac{4}{9} \\times \\frac{3}{8} = \\frac{60}{720} = \\frac{1}{12}§.',
    },
    {
      type: 'timed',
      timeLimit: 90,
      question:
        'If P(A) = 0.6 and P(B) = 0.5, and A and B are independent, what is P(A and B)?',
      options: ['0.30', '0.25', '0.40', '0.55', '1.1'],
      correct: 0,
      explanation:
        'For independent events: §P(A \\text{ and } B) = P(A) \\times P(B) = 0.6 \\times 0.5 = 0.30§.',
    },
    {
      type: 'timed',
      timeLimit: 100,
      question:
        'A box contains 5 red and 3 blue marbles. If two marbles are drawn with replacement, what is the probability both are red?',
      options: ['25/64', '5/16', '10/32', '15/64', '1/4'],
      correct: 0,
      explanation:
        'With replacement: §P(\\text{red, red}) = (5/8) \\times (5/8) = 25/64§.',
    },
    {
      type: 'timed',
      timeLimit: 90,
      question:
        'The probability of rain on any given day is 0.2. What is the probability it does NOT rain?',
      options: ['0.8', '0.2', '0.5', '0.4', '0.6'],
      correct: 0,
      explanation:
        'Complement: §P(\\text{not rain}) = 1 - P(\\text{rain}) = 1 - 0.2 = 0.8§.',
    },

    {
      type: 'untimed',
      timeLimit: 90,
      question:
        'A fair coin is flipped three times. What is the probability of getting exactly two heads?',
      options: ['1/8', '3/8', '1/2', '5/8', '3/4'],
      correct: 1,
      explanation:
        'Total outcomes: §2^3 = 8§ (HHH, HHT, HTH, HTT, THH, THT, TTH, TTT). Exactly 2 heads: HHT, HTH, THH = 3 outcomes. §P(\\text{exactly 2 heads}) = \\frac{3}{8}§.',
    },
    {
      type: 'untimed',
      timeLimit: 100,
      question:
        'In a bag of 12 marbles (5 red, 4 blue, 3 green), what is the probability of randomly selecting a red or green marble?',
      options: ['5/12', '1/3', '2/3', '1/2', '8/12'],
      correct: 2,
      explanation:
        'Red or green: §5 + 3 = 8§ marbles. Total: 12 marbles. §P(\\text{red or green}) = \\frac{8}{12} = \\frac{2}{3}§.',
    },
    {
      type: 'untimed',
      timeLimit: 120,
      question:
        'Two dice are rolled. What is the probability that the sum is 7?',
      options: ['1/12', '1/9', '1/6', '1/3', '5/36'],
      correct: 2,
      explanation:
        'Ways to get sum of 7: (1,6), (2,5), (3,4), (4,3), (5,2), (6,1) = 6 ways. Total outcomes: §6 \\times 6 = 36§. §P(\\text{sum} = 7) = \\frac{6}{36} = \\frac{1}{6}§.',
    },
    {
      type: 'untimed',
      timeLimit: 110,
      question:
        'A card is drawn from a standard 52-card deck. What is the probability of NOT drawing a spade?',
      options: ['1/4', '1/3', '1/2', '3/4', '2/3'],
      correct: 3,
      explanation:
        '§P(\\text{spade}) = \\frac{13}{52} = \\frac{1}{4}§. §P(\\text{not spade}) = 1 - \\frac{1}{4} = \\frac{3}{4}§.',
    },
    {
      type: 'untimed',
      timeLimit: 130,
      question:
        'Box A has 3 red and 2 blue balls. Box B has 4 red and 1 blue ball. One ball is randomly selected from each box. What is the probability both are red?',
      options: ['7/25', '12/25', '3/5', '7/10', '4/5'],
      correct: 1,
      explanation:
        '§P(\\text{red from A}) = \\frac{3}{5}§. §P(\\text{red from B}) = \\frac{4}{5}§. Events are independent. §P(\\text{both red}) = \\frac{3}{5} \\times \\frac{4}{5} = \\frac{12}{25}§.',
    },
    {
      type: 'untimed',
      timeLimit: 115,
      question:
        'A fair die is rolled twice. What is the probability that the sum is 7?',
      options: ['1/6', '1/4', '1/8', '1/12', '1/3'],
      correct: 0,
      explanation:
        'Favorable outcomes: (1,6), (2,5), (3,4), (4,3), (5,2), (6,1) = 6 outcomes. Total = 36. §P = \\frac{6}{36} = \\frac{1}{6}§.',
    },
    {
      type: 'untimed',
      timeLimit: 120,
      question:
        'In a deck of 52 cards, what is the probability of drawing an Ace or a King?',
      options: ['2/13', '1/13', '4/13', '1/26', '8/52'],
      correct: 0,
      explanation:
        'Aces = 4, Kings = 4. Total favorable = 8. §P = \\frac{8}{52} = \\frac{2}{13}§.',
    },
    {
      type: 'untimed',
      timeLimit: 110,
      question:
        'A bag contains 5 red, 3 blue, and 2 yellow marbles. If two marbles are drawn without replacement, what is the probability both are red?',
      options: ['1/4', '2/9', '1/5', '1/3', '4/15'],
      correct: 1,
      explanation:
        'Total: 10 marbles. §P(\\text{first red}) = \\frac{5}{10} = \\frac{1}{2}§. After removing one red: §P(\\text{second red|first red}) = \\frac{4}{9}§. §P(\\text{both red}) = \\frac{1}{2} \\times \\frac{4}{9} = \\frac{4}{18} = \\frac{2}{9}§.',
    },
    {
      type: 'untimed',
      timeLimit: 120,
      question:
        'In a class of 30 students, 18 play soccer and 12 play basketball. If 8 students play both sports, what is the probability a randomly selected student plays at least one sport?',
      options: ['11/15', '3/5', '4/5', '7/10', '2/3'],
      correct: 0,
      explanation:
        'Play at least one = soccer OR basketball. §P(S \\text{ or } B) = P(S) + P(B) - P(S \\text{ and } B) = \\frac{18}{30} + \\frac{12}{30} - \\frac{8}{30} = \\frac{22}{30} = \\frac{11}{15}§.',
    },
    {
      type: 'untimed',
      timeLimit: 110,
      question:
        'From a group of 6 people, how many different 2-person teams can be formed?',
      options: ['12', '15', '18', '21', '30'],
      correct: 1,
      explanation:
        'Order does not matter, use combinations: §C(6,2) = \\frac{6!}{2! \\times 4!} = \\frac{6 \\times 5}{2 \\times 1} = \\frac{30}{2} = 15§.',
    },
    {
      type: 'untimed',
      timeLimit: 130,
      question:
        'A password must be 4 digits long using digits 0-9 without repetition. How many passwords start with an odd digit?',
      options: ['2520', '3024', '4536', '5040', '6048'],
      correct: 0,
      explanation:
        'First digit: 5 odd digits (1,3,5,7,9). Second digit: 9 remaining. Third: 8 remaining. Fourth: 7 remaining. Total = §5 \\times 9 \\times 8 \\times 7 = 2520§.',
    },
    {
      type: 'untimed',
      timeLimit: 130,
      question:
        'A jar contains 10 balls numbered 1-10. If 3 balls are drawn without replacement, what is the probability all three numbers are even?',
      options: ['1/12', '1/10', '1/8', '1/6', '1/5'],
      correct: 0,
      explanation:
        'Even numbers: 2,4,6,8,10 = 5 balls. §P(\\text{all even}) = \\frac{C(5,3)}{C(10,3)} = \\frac{10}{120} = \\frac{1}{12}§. Or: §\\frac{5}{10} \\times \\frac{4}{9} \\times \\frac{3}{8} = \\frac{60}{720} = \\frac{1}{12}§.',
    },
    {
      type: 'untimed',
      timeLimit: 90,
      question:
        'If P(A) = 0.6 and P(B) = 0.5, and A and B are independent, what is P(A and B)?',
      options: ['0.30', '0.25', '0.40', '0.55', '1.1'],
      correct: 0,
      explanation:
        'For independent events: §P(A \\text{ and } B) = P(A) \\times P(B) = 0.6 \\times 0.5 = 0.30§.',
    },
    {
      type: 'untimed',
      timeLimit: 100,
      question:
        'A box contains 5 red and 3 blue marbles. If two marbles are drawn with replacement, what is the probability both are red?',
      options: ['25/64', '5/16', '10/32', '15/64', '1/4'],
      correct: 0,
      explanation:
        'With replacement: §P(\\text{red, red}) = (5/8) \\times (5/8) = 25/64§.',
    },
    {
      type: 'untimed',
      timeLimit: 90,
      question:
        'The probability of rain on any given day is 0.2. What is the probability it does NOT rain?',
      options: ['0.8', '0.2', '0.5', '0.4', '0.6'],
      correct: 0,
      explanation:
        'Complement: §P(\\text{not rain}) = 1 - P(\\text{rain}) = 1 - 0.2 = 0.8§.',
    },
    {
      type: 'untimed',
      questionType: 'quantitative_comparison',
      question:
        'A fair six-sided die is rolled once. Compare the two quantities.',
      quantityA: 'Probability of an even number',
      quantityB: 'Probability of a number greater than 4',
      correct: 0,
      explanation:
        'Compare the two quantities directly. Quantity A is greater.',
    },
    {
      type: 'untimed',
      questionType: 'multiple_choice_multi',
      question: 'Which events have probability §1/2§? Select all that apply.',
      options: [
        'Flipping heads on a fair coin',
        'Rolling an even number on a fair die',
        'Rolling a 6 on a fair die',
        'Drawing a red card from a standard deck',
        'Rolling a number less than 3 on a fair die',
      ],
      correct: [0, 1, 3],
      explanation:
        'Heads, even die outcomes, and red cards each represent half of the equally likely outcomes.',
    },
    {
      type: 'timed',
      timeLimit: 105,
      questionType: 'numeric_entry',
      question: 'How many 3-person committees can be formed from 6 people?',
      correctAnswer: '20',
      acceptedAnswers: ['20'],
      explanation: 'Use combinations: §C(6,3)=6*5*4/(3*2*1)=20§.',
    },
  ];

  const quickReference = [
    '§P(A) = \\frac{\\text{Favorable}}{\\text{Total}}§',
    '§P(A \\text{ and } B) = P(A) \\times P(B)§ for independent',
    'Without replacement: update the numerator and denominator',
    '§P(A \\text{ or } B) = P(A) + P(B) - P(A \\text{ and } B)§',
    'Permutations: §n!/(n-r)!§',
    'Restrictions first: fill constrained slots before open slots',
    'Combinations: §n!/(r!(n-r)!)§',
  ];

  const getStartedWithProps = {
    title: 'Need help with Probabilities?',
    spaceName: 'Quant hacks',
    humanCoachTitle: 'Talk to a GRE coach',
    liwazaAITitle: 'Ask AI',
    communityTitle: 'Ask other students',
    showHumanCoach: true,
    showLiwazaAI: true,
    showCommunity: true,
    aifirstmessage:
      'I need help understanding Probability concepts for the GRE Quantitative section',
    aicontext:
      'Master basic probability, compound events, conditional probability, and counting techniques',
  };

  return (
    <LessonTemplate
      lessonId="probabilities"
      lessonTitle="Probabilities"
      lessonIcon={<FiBarChart />}
      lessonDescription="Master basic probability, compound events, conditional probability, and counting techniques"
      sections={sections}
      practiceQuestions={practiceQuestions}
      quickReference={quickReference}
      getStartedWithProps={getStartedWithProps}
      supplementalPracticeSlot={
        <QuantLessonPracticeSlot lessonId="probabilities" />
      }
    />
  );
};

export default ProbabilitiesLesson;
